import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import { useHasPermission } from "@/hooks/useHasPermission";
import type { ChildRouteContext } from "@/root";
import { throwIfHasNoPermission } from "@/utils/permissions";

import CheckinFormList from "@/modules/performance/admin/components/checkin-form/CheckinFormList";
import ObjectivePeriodList from "@/modules/performance/admin/components/objective-period/ObjectivePeriodList";
import { fetchObjectivePeriodsQueryOptions } from "@/modules/performance/admin/services";
import { ObjectivePeriodStatuses } from "@/modules/performance/shared/constants";

const tabs = [...ObjectivePeriodStatuses, "form"] as const;

const searchSchema = z.object({
  tab: z.enum(tabs).optional(),
});

export const Route = createFileRoute(
  "/admin/_panel/_performance/performance/objectives/periods/",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) => {
    throwIfHasNoPermission(
      context.user,
      "performance.performance-objective-period.view",
    );

    return {
      crumb: [
        {
          label: context.t("Objective-Setting Periods", {
            ns: "performance.admin",
          }),
        },
      ],
    } satisfies ChildRouteContext;
  },
  loader: (opts) =>
    opts.context.queryClient.prefetchQuery(fetchObjectivePeriodsQueryOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { tab: status } = Route.useSearch();
  const { t } = useTranslation("performance.admin");
  const hasPermission = useHasPermission();
  return (
    <Container
      title={t("Objective-Setting Periods")}
      description={t(
        "With this section, you can create a objective-setting period for users.",
      )}
    >
      <TabGroup
        defaultIndex={status ? tabs.indexOf(status) + 1 : 0}
        onChange={(index) =>
          navigate({
            search:
              index === 0
                ? {}
                : {
                    tab: tabs[index - 1],
                  },
          })
        }
      >
        <TabList>
          <Tab>{t("All")}</Tab>
          <Tab>{t("Active")}</Tab>
          <Tab>{t("Finished")}</Tab>
          <Tab>{t("Pending")}</Tab>
          {hasPermission("performance.performance-objective-period.view") && (
            <Tab>{t("Meeting Template")}</Tab>
          )}
        </TabList>
        <TabPanels>
          <TabPanel>
            <ObjectivePeriodList />
          </TabPanel>
          <TabPanel>
            <ObjectivePeriodList status="active" />
          </TabPanel>
          <TabPanel>
            <ObjectivePeriodList status="finished" />
          </TabPanel>
          <TabPanel>
            <ObjectivePeriodList status="pending" />
          </TabPanel>
          {hasPermission("performance.performance-checkin-form.view") && (
            <TabPanel>
              <CheckinFormList />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>
    </Container>
  );
}
